import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as R from 'ramda'
import * as L from '@/components/organisms/my-bookings/lenses'
import { API_MANAGEMENT_ORDERS_V2, ORDERS_FIELDS } from '@/constants/urls'
import { axiosWcgw } from '@/services/http/axiosApi'

import { SERVER_FORMAT, API_V2_FORMAT, now, getDateInstance } from '@/helpers/date-helpers'
import { isLuxuryBrand } from '@/constants/brands'
import { getCurrency } from '@/helpers/currency'

// This store is used only by vue2 at the moment, do not delete until migration is complete

enum OrdersState {
  LOADING = 'Loading',
  READY = 'Ready',
  INIT = 'Init',
  ERROR = 'Error'
}

const sum = (lens, premapStep = R.identity, defaultValue = 0) =>
  R.pipe(
    R.view(L.orderItems),
    premapStep,
    R.map(R.o(R.defaultTo(defaultValue), R.view(lens))),
    R.sum
  )
interface AxiosGetParams {
  sortType?: string
  filter?: string
  startDate?: string
}

const axiosGet = async (params: AxiosGetParams) => {
  const { data } = await axiosWcgw.get(API_MANAGEMENT_ORDERS_V2, {
    params: {
      currency: getCurrency(),
      fields: String(ORDERS_FIELDS),
      ...params
    }
  })

  return data?.orders
}
const getOrdersFromApi = () =>
  axiosGet({
    sortType: 'desc',
    filter:
      'orderItems:(orderType=HOTEL_BOOKING+hotelBooking.hotel.name!=[null]);$:orderItems!=[empty]'
  })

const getActiveOrdersFromApi = () =>
  axiosGet({
    filter:
      'orderItems:(hotelBooking!=[null]+hotelBooking.status!=CANCELLED+hotelBooking.status!=OBSOLETE);$:orderItems!=[empty]',
    startDate: now().format(SERVER_FORMAT),
    sortType: 'asc'
  })

export const useBookingsStore = defineStore('bookings', () => {
  const orders: any = ref([])
  const activeOrders: any = ref([])
  const ordersInfoState = ref(OrdersState.INIT)
  const activeOrdersInfoState = ref(OrdersState.INIT)

  const setOrdersInfoState = (value: OrdersState) => {
    ordersInfoState.value = value
  }

  const setActiveOrdersInfoState = (value: OrdersState) => {
    activeOrdersInfoState.value = value
  }

  const setActiveOrders = (value: any) => {
    activeOrders.value = value
  }

  const setOrders = (value: any) => {
    orders.value = value
  }

  const load = async () => {
    setOrdersInfoState(OrdersState.LOADING)

    try {
      const orders = await getOrdersFromApi()
      setOrders(orders)
      setOrdersInfoState(OrdersState.READY)
    } catch {
      setOrdersInfoState(OrdersState.ERROR)
    }
  }

  const getActiveOrders = async () => {
    setActiveOrdersInfoState(OrdersState.LOADING)

    try {
      const result = await getActiveOrdersFromApi()
      setActiveOrders(result)
      setActiveOrdersInfoState(OrdersState.READY)
    } catch {
      setActiveOrdersInfoState(OrdersState.ERROR)
    }
  }

  const nextOrderId = computed(() => activeOrders.value[0]?.id)
  const nextOrderNumberRoom = computed(() =>
    sum(L.quantity$, R.chain(R.view(L.roomStays$$)), 1)(activeOrders.value[0])
  )
  const nextOrderHotelBooking = computed(() => activeOrders.value[0]?.orderItems[0]?.hotelBooking)
  const nextOrderHotel = computed(() => nextOrderHotelBooking.value?.hotel || {})
  const nextOrderReservation = computed(() => nextOrderHotelBooking.value?.reservation || {})
  const nextOrderAdultCount = computed(() =>
    activeOrders.value[0]?.orderItems.reduce((acc, curr) => {
      return acc + curr.hotelBooking.reservation.stayInfo.adultCount
    }, 0)
  )
  const nextOrderChildCount = computed(() =>
    activeOrders.value[0]?.orderItems.reduce((acc, curr) => {
      return acc + curr.hotelBooking.reservation.stayInfo.childCount
    }, 0)
  )
  const nextOrderOCIAvailable = computed(() => {
    const { welcomeInfo: { welcomeUrl, checkinStatus, dateMin, dateMax } = {} } =
      nextOrderReservation.value
    const isOCI = !!welcomeUrl && checkinStatus === 'YES'

    const todayDate = now()
    const ociOpeningDate = getDateInstance()(dateMin, API_V2_FORMAT)
    const ociClosingDate = getDateInstance()(dateMax, API_V2_FORMAT)
    const isHappeningNow =
      todayDate.isSameOrAfter(ociOpeningDate) && todayDate.isSameOrBefore(ociClosingDate)

    return isOCI && isHappeningNow
  })
  const nextOrderIsLuxuryBrand = computed(() => isLuxuryBrand(nextOrderHotel.value?.brand?.code))

  const activeOrdershasErrors = computed(() => activeOrdersInfoState.value === OrdersState.ERROR)

  const isActiveOrdersLoading = computed(() => {
    const state = activeOrdersInfoState.value
    return state === OrdersState.INIT || state === OrdersState.LOADING
  })

  return {
    activeOrders,
    load,
    getActiveOrders,
    nextOrderId,
    nextOrderNumberRoom,
    nextOrderHotelBooking,
    nextOrderHotel,
    nextOrderReservation,
    nextOrderAdultCount,
    nextOrderChildCount,
    nextOrderOCIAvailable,
    nextOrderIsLuxuryBrand,
    activeOrdershasErrors,
    isActiveOrdersLoading
  }
})
